import React, { useState, useRef } from "react";
import { connect } from "react-redux";
import styled from "@emotion/styled";
import { doToggleFullscreen } from "../ducks";

const IMAGE_SIZE = determineImageSize();

const select = ({ interactiveMap }) => ({
  fullscreen: interactiveMap.fullscreen
});

const actions = {
  toggleFullscreen: doToggleFullscreen
};

function Slider({ children, fullscreen, toggleFullscreen }) {
  const [index, setIndex] = useState(0);
  const scrollRef = useRef();
  return (
    <Container fullScreen={fullscreen}>
      <ScrollableChildren
        ref={scrollRef}
        fullScreen={fullscreen}
        onScroll={e => autoScrollToNearestImageAfterPause(e, setIndex)}
      >
        {children.map(image => (
          <ImageWrapper fullScreen={fullscreen}>{image}</ImageWrapper>
        ))}
      </ScrollableChildren>

      <DotNavigation
        numDots={children.length}
        activeIndex={index}
        parent={scrollRef}
      />
      <ShowFullScreenButton onClick={toggleFullscreen} />
      <BackgroundOverlay hide={!fullscreen} onClick={(toggleFullscreen)}/>
    </Container>
  );
}

export default connect(
  select,
  actions
)(Slider);

const ImageWrapper = styled.div`
  position: relative;
  ${({ fullScreen }) =>
    fullScreen
      ? `
      min-height: calc(50vw / 1.5);
      max-height: calc(50vw / 1.5);
      min-width: 50vw;
      max-width: 50vw;
      overflow: hidden;
      img {
        object-fit: cover;
        height: 100%;
      }
      @media (max-width: 800px) {
        max-width: 100%;
        min-width: 100%;
        min-height: calc(100vw / 1.5);
      }
      `
      : `
    width: 40vw;
    min-width: 40vw;
    @media (min-width: 600px) {
      img {
        height: 50vh;
      }
    }
    `}

  @media (max-width: 600px) {
    min-width: 100vw;
  }
`;

const Container = styled.div`
  position: relative;
  ${({ fullScreen }) =>
    fullScreen &&
    `position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    box-sizing: border-box;
    width: 100vw;
    z-index: 1000;
    @media (min-width: 800px) {
      padding: 100px;
    }
    display: flex;
    // justify-content: center;
    align-items: center;
  `}
`;

function ShowFullScreenButton(props) {
  return (
    <ShowFullScreenButtonWrapper {...props}>
      <ExpandIcon src="https://image.flaticon.com/icons/png/512/70/70368.png" />
    </ShowFullScreenButtonWrapper>
  );
}

const BackgroundOverlay = styled.div`
  position: fixed;
  z-index: -1;
  background-color: rgba(0, 0, 0, 0.9);
  height: 100vh;
  width: 100vw;
  top: 0;
  left: 0;
  display: ${({ hide }) => (hide ? "none" : "block")};
`;

const ShowFullScreenButtonWrapper = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: white;
  box-shadow: rgba(0, 0, 0, 0.3) 0px 1px 4px -1px;
  padding: 0.5em;
  padding-bottom: 0.2em;
  border-radius: 2px;
  cursor: pointer;
  z-index: 1001;
`;

const ExpandIcon = styled.img`
  width: 20px;
  filter: opacity(70%);
`;

const ScrollableChildren = styled(Container)`
  display: flex;
  overflow-x: scroll;
  -webkit-overflow-scrolling: touch;
`;

function DotNavigation({ numDots, activeIndex, parent }) {
  const arrOfNumDots = Array(numDots).fill(null);
  return (
    <DotContainer>
      {arrOfNumDots.map((r, i) => (
        <Dot
          active={i === activeIndex}
          onClick={() => scrollToImageByIndex(parent.current, i)}
        />
      ))}
    </DotContainer>
  );
}

const DotContainer = styled.div`
  position: absolute;
  bottom: 1em;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
`;

const Dot = styled.div`
  width: 10px;
  height: 10px;
  border: 1px solid rgba(255, 255, 255, 0.5);
  background-color: rgba(
    255,
    255,
    255,
    ${({ active }) => (active ? 0.5 : 0.2)}
  );
  border-radius: 10px;
  margin: 0em 0.2em;
  cursor: pointer;
`;

let timer = null;
function autoScrollToNearestImageAfterPause(e, callback) {
  e.persist();
  const PAUSE_TIME = 200;
  if (timer !== null) clearTimeout(timer);
  timer = setTimeout(() => scrollToNearbyImage(e.target, callback), PAUSE_TIME);
}

function scrollToNearbyImage(parent, callback) {
  const scrollPosition = parent.scrollLeft;
  const nearestIndex = calculateNearestIndex(scrollPosition);
  // if (!isMobile()) scrollToImageByIndex(parent, nearestIndex);
  callback(nearestIndex);
}

function calculateNearestIndex(scrollPosition) {
  const lastFullySeenIndex = getLastFullySeenIndex(scrollPosition);
  const percentOverCurrentIndex = getPercentOverCurrentIndex(scrollPosition);
  return isOverEnoughToGoToNextImage(percentOverCurrentIndex)
    ? lastFullySeenIndex + 1
    : lastFullySeenIndex;
}

function getLastFullySeenIndex(scrollPosition) {
  return Math.floor(scrollPosition / IMAGE_SIZE);
}

function getPercentOverCurrentIndex(scrollPosition) {
  return ((scrollPosition % IMAGE_SIZE) / IMAGE_SIZE) * 100;
}

function isOverEnoughToGoToNextImage(percentOverCurrentIndex) {
  const PERCENT_THRESHOLD = 50;
  return percentOverCurrentIndex > PERCENT_THRESHOLD;
}

function scrollToImageByIndex(component, index) {
  component.scrollTo({
    top: 0,
    left: index * IMAGE_SIZE,
    behavior: "smooth"
  });
}

function determineImageSize() {
  return isMobile() ? getMobileWidth() : getDesktopWidth();
}

function isMobile() {
  return window.innerWidth < 600;
}

function getMobileWidth() {
  return window.innerWidth;
}

function getDesktopWidth() {
  return window.innerHeight * 0.5 * 1.5;
}
